import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { CircleNotch } from '@phosphor-icons/react';

import { cn } from '../../utils/cn';

const buttonVariants = cva(
  'items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 focus-visible:ring-offset-2 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        primary:
          'inline-flex items-center gap-space-2 rounded-ds-full bg-gradient-to-br from-sage-500 to-sage-600 px-space-5 py-space-2-5 text-type-body font-body font-semibold text-sage-50 shadow-[0_8px_20px] shadow-sage-glow hover:from-sage-600 hover:to-sage-700 disabled:opacity-45',
        secondary:
          'inline-flex items-center gap-space-2 rounded-ds-full bg-sage-100 dark:bg-sage-800/40 border border-sage-200 dark:border-sage-700 px-space-5 py-space-2-5 text-type-body font-body font-semibold text-sage-700 dark:text-sage-300 hover:bg-sage-200 disabled:opacity-45',
        ghost:
          'inline-flex items-center gap-space-2 rounded-ds-full px-space-5 py-space-2-5 text-type-body font-body font-medium text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 disabled:opacity-45',
        destructive:
          'inline-flex items-center gap-space-2 rounded-ds-full bg-gradient-to-br from-danger-500 to-danger-700 px-space-5 py-space-2-5 text-type-body font-body font-semibold text-stone-50 shadow-[0_8px_20px] shadow-danger-glow hover:bg-danger-700 disabled:opacity-45',
        outline:
          'inline-flex items-center gap-space-2 rounded-ds-full bg-sage-100 dark:bg-sage-800/40 border border-sage-200 dark:border-sage-700 px-space-5 py-space-2-5 text-type-body font-body font-semibold text-sage-700 dark:text-sage-300 hover:bg-sage-200 disabled:opacity-45',
      },
      size: {
        sm: 'px-space-3 py-space-2',
        md: 'px-space-5 py-space-2-5',
        lg: 'px-space-6 py-space-3',
        icon: 'size-10 rounded-ds-full p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export const Button = forwardRef(function Button(
  { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-ds-id={
        variant === 'outline' ? undefined : `ds/button/${variant}--${size}`
      }
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <CircleNotch className="h-4 w-4 animate-spin" aria-hidden /> : null}
          {children}
        </>
      )}
    </Comp>
  );
});

export { buttonVariants };
